// @flow
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, generatePath } from 'react-router-dom';
import { isDokkaSupportSelector } from 'domain/env';
import ROUTES_PATH from 'domain/router/routesPathConfig';

import { DrawerStatic, DrawerHeader, DrawerFooter } from 'components/mui/Layouts/components/Drawer';
import { LayoutRoot, LayoutContent } from 'components/mui/Layouts/components/Layout';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import { AppBarItemDivider, AppBarStatic } from 'components/mui/Layouts/components/AppBar';
import SettingsLink from 'components/mui/Layouts/components/Drawer/SettingsLink';
import HelpDropdown from 'components/mui/Layouts/components/HelpDropdown';
import LocaleSelect from 'components/mui/LocaleSelect';
import AppBarLogo from 'components/mui/Layouts/Companies/components/Logo';
import AppBarSearch from 'components/mui/Layouts/Companies/components/Search';
import AccountDropdown from 'components/mui/Layouts/components/AccountDropdown';
import FullscreenIconButton from 'components/mui/Layouts/components/FullscreenIconButton';
import BackToArrowBase from 'components/mui/Layouts/components/BackToArrowBase';
import AsideLogo from 'components/mui/Layouts/components/AsideLogo';

type TLayoutSettings = {
  children: React$Node,
};

const LayoutOrganizationSettings: React$StatelessFunctionalComponent<TLayoutSettings> = ({ children }) => {
  const isGhost = useSelector(isDokkaSupportSelector);
  const ROUTE_ID = isGhost ? ROUTES_PATH.ORGANIZATIONS.id : ROUTES_PATH.COMPANIES.id;
  const path = generatePath(ROUTES_PATH[ROUTE_ID].absolute);

  return (
    <LayoutRoot display="flex" flexGrow="1">
      <DrawerStatic variant="permanent">
        <DrawerHeader>
          <Link to={path}>
            <AsideLogo />
          </Link>
        </DrawerHeader>
        <DrawerFooter>
          <List>
            <SettingsLink />
          </List>
        </DrawerFooter>
      </DrawerStatic>

      <AppBarStatic>
        <Box flexShrink={0} display="flex" alignItems="center">
          <BackToArrowBase
            path={path}
            tooltip={{ id: 'configurations.org.back', defaultMessage: 'Back to Company List' }}
          />
          <AppBarLogo />
        </Box>
        <Box flexShrink={1} flexGrow={1} display="flex" justifyContent="center">
          <AppBarSearch />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} flexShrink={0} ml="auto">
          <FullscreenIconButton />
          <AppBarItemDivider />
          <HelpDropdown />
          <LocaleSelect flagOnly />
          <AccountDropdown />
        </Stack>
      </AppBarStatic>

      <LayoutContent>
        <DrawerHeader />
        {children}
      </LayoutContent>
    </LayoutRoot>
  );
};

export default LayoutOrganizationSettings;
